import { useTranslation } from "react-i18next";
import { TextField } from "@/shared/ui/core";
import { RepeatableList, TextAreaField } from "@/features/admin/components";
import { EditorialBlock, type EditorialBlockLocaleProps } from "./EditorialBlock";
import { useBlockFilled } from "./useBlockFilled";

/**
 * Bloque 6 · La tabla nutrimental.
 *
 * La CANTIDAD es texto y no un número, a propósito: hay dosis que son
 * "150-300", "c.s.p." o "<0.1". Forzarlas a número obligaría a inventar
 * un valor donde la etiqueta real no da uno.
 *
 * Es la lista con más techo del contrato —cuarenta filas— porque una
 * tabla nutrimental completa las tiene.
 */
export function EditorialSpecTableBlock({ locale }: EditorialBlockLocaleProps) {
  const { t } = useTranslation();
  const name = `editorial.${locale}.specTable`;
  const filled = useBlockFilled(name);

  return (
    <EditorialBlock
      name={name}
      title={t("Tabla nutrimental")}
      description={t("Una fila por ingrediente, con su cantidad y su función.")}
      filled={filled}
    >
      <TextField
        name={`${name}.title`}
        label={t("Título del bloque")}
        placeholder={t("Información nutrimental")}
      />
      <TextField
        name={`${name}.type`}
        label={t("Tipo de tabla")}
        placeholder={t("Análisis por porción")}
      />
      <TextAreaField
        name={`${name}.footer`}
        label={t("Nota al pie")}
        rows={2}
        hint={t("El texto pequeño bajo la tabla.")}
        fieldClassName="sm:col-span-2"
      />

      <div className="sm:col-span-2">
        <RepeatableList
          name={`${name}.items`}
          max={40}
          addLabel={t("Añadir fila")}
          itemLabel={(index) => t("Fila {{n}}", { n: index + 1 })}
          emptyHint={t("Todavía no hay filas. Añade la primera.")}
          newItem={() => ({ name: "", dose: "", unit: "", function: "" })}
        >
          {({ name: row }) => (
            <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-4">
              <TextField
                name={`${row}.name`}
                label={t("Ingrediente")}
                fieldClassName="sm:col-span-2"
              />
              <TextField
                name={`${row}.dose`}
                label={t("Cantidad")}
                placeholder="150-300"
                hint={t("Texto libre: cabe «c.s.p.».")}
              />
              <TextField name={`${row}.unit`} label={t("Unidad")} placeholder="mg" />
              <TextField
                name={`${row}.function`}
                label={t("Función")}
                fieldClassName="sm:col-span-4"
              />
            </div>
          )}
        </RepeatableList>
      </div>
    </EditorialBlock>
  );
}
